import React, { useState } from 'react';
import { PlusIcon, Trash2Icon, PencilIcon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useRegistry, type JobDescription, type JobStatus } from '../../contexts/RegistryContext';
import { formatWhen } from '../../utils/adminFormat';
type Draft = Omit<JobDescription, 'id' | 'reference' | 'createdAt' | 'updatedAt'> & {
  id?: string;
};
const blank: Draft = {
  title: '',
  organization: '',
  location: '',
  employmentType: 'Permanent',
  compensation: '',
  summary: '',
  responsibilities: '',
  requirements: '',
  status: 'draft'
};
const statusLabels: Record<JobStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  closed: 'Closed'
};
const field = 'mt-2 w-full border border-navy-100 bg-white px-3.5 py-2.5 font-sans text-[0.875rem] text-ink placeholder:text-charcoal-200 transition-colors duration-120 hover:border-navy-300 focus:border-navy-500';
const label = 'block font-sans text-[0.8125rem] font-medium text-ink';
export function JobsPanel() {
  const {
    jobs,
    assignments,
    saveJob,
    setJobStatus,
    removeJob
  } = useRegistry();
  const [draft, setDraft] = useState<Draft | null>(null);
  const set = (key: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setDraft(prev => prev ? {
    ...prev,
    [key]: e.target.value
  } : prev);
  return <section className="mt-8" data-id="element-128" data-mp="uagi5z13uvpu">
      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-129" data-mp="zbw5xyuo3gbx">
        <p className="font-sans text-[0.875rem] text-charcoal-500" data-id="element-130" data-mp="z9tdl861lb41">
          Write a description once, then invite registered executives to apply to it from the Assignments tab.
        </p>
        {!draft ? <Button size="sm" onClick={() => setDraft({
        ...blank
      })} data-id="element-131" data-mp="1d3140wie00k5">
            <PlusIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-132" data-mp="1d29di03nct0q" />
            New job description
          </Button> : null}
      </div>

      {draft ? <form className="mt-6 border-l-2 border-ink bg-white p-6" onSubmit={e => {
      e.preventDefault();
      if (!draft.title.trim()) return;
      saveJob(draft);
      setDraft(null);
    }} data-id="element-133" data-mp="1n29xzpd95f6i">
          <div className="flex items-center justify-between gap-4" data-id="element-134" data-mp="172w5qmp8buxb">
            <h2 className="font-serif text-[1.25rem] text-ink" data-id="element-135" data-mp="174794lerj0nw">
              {draft.id ? 'Edit job description' : 'New job description'}
            </h2>
            <button type="button" onClick={() => setDraft(null)} className="inline-flex items-center gap-1.5 font-sans text-[0.8125rem] text-charcoal-500 underline underline-offset-2 hover:text-ink" data-id="element-136" data-mp="1g2rmd71k0i4q">
              <XIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-137" data-mp="1pzntb8bt0w76" />
              Cancel
            </button>
          </div>

          <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-138" data-mp="3rstvwn6eqdz3">
            <div className="sm:col-span-2" data-id="element-139" data-mp="3rtddu3w252d4">
              <label htmlFor="jd-title" className={label} data-id="element-140" data-mp="3muk0cbgrtbcy">
                Job title <span className="text-gold-700" data-id="element-141" data-mp="3mwd8jv7183n2">*</span>
              </label>
              <input id="jd-title" required value={draft.title} onChange={set('title')} className={field} placeholder="Chief Operating Officer" data-id="element-142" data-mp="22chsw7fnwkkv" />
            </div>
            <div data-id="element-143" data-mp="1smycd8ff92t6">
              <label htmlFor="jd-org" className={label} data-id="element-144" data-mp="1sldfvd9jr9so">
                Client organization
              </label>
              <input id="jd-org" value={draft.organization} onChange={set('organization')} className={field} placeholder="Leave blank if confidential" data-id="element-145" data-mp="1xjondhcdd87q" />
            </div>
            <div data-id="element-146" data-mp="1no4wxagkr1qd">
              <label htmlFor="jd-location" className={label} data-id="element-147" data-mp="1non24wl7sqaf">
                Location
              </label>
              <input id="jd-location" value={draft.location} onChange={set('location')} className={field} placeholder="London, United Kingdom" data-id="element-148" data-mp="19p58a2t2amu7" />
            </div>
            <div data-id="element-149" data-mp="3412385qpqau0">
              <label htmlFor="jd-type" className={label} data-id="element-150" data-mp="342mzq0wl83ui">
                Employment type
              </label>
              <select id="jd-type" value={draft.employmentType} onChange={set('employmentType')} className={field} data-id="element-151" data-mp="38zwn7b0f25fp">
                {['Permanent', 'Interim', 'Board appointment', 'Fixed term', 'Advisory'].map(t => <option key={t} data-id="element-152" data-mp="13wzo578tbw56">{t}</option>)}
              </select>
            </div>
            <div data-id="element-153" data-mp="zzbm7k8pd33s">
              <label htmlFor="jd-comp" className={label} data-id="element-154" data-mp="uynx5441awc7">
                Compensation
              </label>
              <input id="jd-comp" value={draft.compensation} onChange={set('compensation')} className={field} placeholder="£250,000–£300,000 plus bonus" data-id="element-155" data-mp="pzujnboqz5c1" />
            </div>
            <div className="sm:col-span-2" data-id="element-156" data-mp="l6rkmo17b3og">
              <label htmlFor="jd-summary" className={label} data-id="element-157" data-mp="gajchulf8aqs">
                Overview
              </label>
              <textarea id="jd-summary" rows={3} value={draft.summary} onChange={set('summary')} className={`${field} resize-y`} data-id="element-158" data-mp="c97jrg7939ed" />
            </div>
            <div className="sm:col-span-2" data-id="element-159" data-mp="7anzrlc7w7on">
              <label htmlFor="jd-resp" className={label} data-id="element-160" data-mp="2artffg1s45x">
                Responsibilities
              </label>
              <textarea id="jd-resp" rows={4} value={draft.responsibilities} onChange={set('responsibilities')} className={`${field} resize-y`} placeholder="One per line" data-id="element-161" data-mp="2dwahx2q3xi0e" />
            </div>
            <div className="sm:col-span-2" data-id="element-162" data-mp="2nwktyk0eojdz">
              <label htmlFor="jd-req" className={label} data-id="element-163" data-mp="2ntf6f97fdpkd">
                Requirements
              </label>
              <textarea id="jd-req" rows={4} value={draft.requirements} onChange={set('requirements')} className={`${field} resize-y`} placeholder="One per line" data-id="element-164" data-mp="1v6knwhu6509j" />
            </div>
            <div data-id="element-165" data-mp="203kuctuxk516">
              <label htmlFor="jd-status" className={label} data-id="element-166" data-mp="247aftagb66hz">
                Status
              </label>
              <select id="jd-status" value={draft.status} onChange={set('status')} className={field} data-id="element-167" data-mp="28xqsutg7vq3t">
                <option value="draft" data-id="element-168" data-mp="1cgckrzqgy9tc">Draft — not yet open</option>
                <option value="open" data-id="element-169" data-mp="1ce073yrqulm3">Open — accepting applications</option>
                <option value="closed" data-id="element-170" data-mp="1gg4z6t2jna6m">Closed</option>
              </select>
            </div>
          </div>

          <div className="mt-7 border-t border-navy-100 pt-5" data-id="element-171" data-mp="2jqr6z7uha11n">
            <Button type="submit" data-id="element-172" data-mp="2euaigcjd9ko1">{draft.id ? 'Save changes' : 'Create job description'}</Button>
          </div>
        </form> : null}

      {!jobs.length && !draft ? <p className="mt-10 border border-dashed border-navy-200 bg-white p-10 text-center font-sans text-[0.875rem] text-charcoal-500" data-id="element-173" data-mp="2ojkcvz1onffb">
          No job descriptions yet. Create one to start inviting registered executives to apply.
        </p> : <ul className="mt-6 space-y-4" data-id="element-174" data-mp="1vwhgbcgkherp">
          {jobs.map(job => {
        const count = assignments.filter(a => a.jobId === job.id).length;
        return <li key={job.id} className="border-l-2 border-ink bg-white p-5" data-id="element-175" data-mp="25s19hr3qxzcr">
                <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-176" data-mp="3l8tcp62ymwfb">
                  <div className="min-w-0" data-id="element-177" data-mp="3h89dlwyttiqj">
                    <div className="flex flex-wrap items-center gap-2.5" data-id="element-178" data-mp="3h7g4ipceah58">
                      <h2 className="font-serif text-[1.125rem] text-ink" data-id="element-179" data-mp="3or8v1x2q9079">{job.title}</h2>
                      <span className="font-sans text-[0.75rem] text-charcoal-300" data-id="element-180" data-mp="3tr4t7foqtxew">{job.reference}</span>
                      <span className={`border px-2 py-0.5 font-sans text-micro uppercase tracking-[0.12em] ${job.status === 'open' ? 'border-state-positive/40 text-state-positive' : job.status === 'draft' ? 'border-gold bg-gold-100 text-gold-700' : 'border-navy-200 text-charcoal-500'}`} data-id="element-181" data-mp="3eu4ag1oj5csk">
                        {statusLabels[job.status]}
                      </span>
                    </div>
                    <p className="mt-1.5 font-sans text-[0.875rem] text-charcoal-700" data-id="element-182" data-mp="uaf3kqztm5mw">
                      {[job.organization || 'Client confidential', job.location, job.employmentType, job.compensation].filter(Boolean).join(' · ')}
                    </p>
                    {job.summary ? <p className="mt-3 max-w-2xl border-l border-navy-100 pl-4 font-serif text-[0.9375rem] leading-relaxed text-charcoal-700" data-id="element-183" data-mp="2qcrxgq9rwe40">
                        {job.summary}
                      </p> : null}
                    <p className="mt-3 font-sans text-[0.75rem] text-charcoal-300" data-id="element-184" data-mp="3e4gx6roxc4uz">
                      {count} invited · updated {formatWhen(job.updatedAt)}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2" data-id="element-185" data-mp="352g13y5ffouw">
                    <select aria-label={`Status for ${job.title}`} value={job.status} onChange={e => setJobStatus(job.id, e.target.value as JobStatus)} className="border border-navy-100 bg-white px-2.5 py-1.5 font-sans text-[0.75rem] text-ink" data-id="element-186" data-mp="304egh8scrhhb">
                      <option value="draft" data-id="element-187" data-mp="1emvp3yrms107">Draft</option>
                      <option value="open" data-id="element-188" data-mp="1jjvv69ohd3rb">Open</option>
                      <option value="closed" data-id="element-189" data-mp="1jiazuhucdgse">Closed</option>
                    </select>
                    <Button size="sm" variant="secondary" onClick={() => setDraft({
                ...job
              })} data-id="element-190" data-mp="1oi73gfz4n67f">
                      <PencilIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-191" data-mp="1tbjflqmmdkja" />
                      Edit
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => removeJob(job.id)} data-id="element-192" data-mp="22awp2jaqu940">
                      <Trash2Icon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-193" data-mp="22cq0sd07rjiv" />
                      <span className="sr-only" data-id="element-194" data-mp="27arf6kmp2uo0">Delete {job.title}</span>
                    </Button>
                  </div>
                </div>
              </li>;
      })}
        </ul>}
    </section>;
}